import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { certificateService } from '@/services/certificate/certificate.service';
import type {
  CertificateTemplate,
  CertificateFieldMapping,
  CreateCertificateTemplateDto,
} from '@/types/certificate.types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Upload, Save, Loader2 } from 'lucide-react';
import CertificateTemplateUpload from '@/components/certificate/certificate-template-upload';
import CertificateFieldMapper from '@/components/certificate/certificate-field-mapper';
import CertificateFormatBuilder from '@/components/certificate/certificate-format-builder';
import ApprovalConditionBuilder from '@/components/certificate/approval-condition-builder';
import ValidityConfiguration from '@/components/certificate/validity-configuration';
import IssuanceConfiguration from '@/components/certificate/issuance-configuration';
import IssuedCertificatesPanel from '@/components/certificate/issued-certificates-panel';
import type { IssuanceType } from '@/types/certificate.types';

const CertificateTemplateManager: React.FC = () => {
  const { processId } = useParams<{
    processId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [templateName, setTemplateName] = useState('');
  const [templateFileUrl, setTemplateFileUrl] = useState('');
  const [certificateNumberFormat, setCertificateNumberFormat] = useState({
    format: 'CERT-{YEAR}-{SEQUENCE}',
  });
  const [approvalCondition, setApprovalCondition] = useState<{
    formId: string;
    questionId: string;
    operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan' | 'notEquals';
    expectedValue: any;
  }>({
    formId: '',
    questionId: '',
    operator: 'equals',
    expectedValue: '',
  });
  const [validityType, setValidityType] = useState<'FOREVER' | 'FIXED_YEARS' | 'CUSTOM'>('FOREVER');
  const [validityYears, setValidityYears] = useState<number | null>(null);
  const [customValidityDays, setCustomValidityDays] = useState<number | null>(null);
  const [issuanceType, setIssuanceType] = useState<IssuanceType>('IMMEDIATE');
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const [applicantCanAccess, setApplicantCanAccess] = useState<boolean>(true);
  const [manualReleaseType, setManualReleaseType] = useState<'STATIC' | 'DYNAMIC' | 'NOT_APPLICABLE'>('NOT_APPLICABLE');
  const [manualReleaseUserId, setManualReleaseUserId] = useState<string | null>(null);
  const [manualReleaseRoles, setManualReleaseRoles] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<CertificateFieldMapping[]>([]);
  const [mapperRefreshKey, setMapperRefreshKey] = useState(0);

  useEffect(() => {
    if (processId) {
      loadTemplate();
    }
  }, [processId]);

  const loadTemplate = async () => {
    if (!processId) return;

    try {
      setLoading(true);
      const existingTemplate = await certificateService.getTemplateByProcess(
        processId,
      );
      
      console.log('[DEBUG] loadTemplate: Template loaded', {
        template: existingTemplate ? JSON.stringify(existingTemplate, null, 2) : null,
        fieldMappings: existingTemplate?.fieldMappings ? JSON.stringify(existingTemplate.fieldMappings, null, 2) : null,
        userProfileFields: existingTemplate?.fieldMappings?.filter((m: CertificateFieldMapping) => m.fieldType === 'USER_PROFILE').map((m: CertificateFieldMapping) => ({
          id: m.id,
          fieldType: m.fieldType,
          userProfileField: m.userProfileField,
        })),
      });
      
      if (existingTemplate) {
        setTemplate(existingTemplate);
        setTemplateName(existingTemplate.name);
        setTemplateFileUrl(existingTemplate.templateFileUrl);
        setCertificateNumberFormat(existingTemplate.certificateNumberFormat as any);
        const loadedCondition = existingTemplate.approvalCondition as any;
        setApprovalCondition(
          loadedCondition && typeof loadedCondition === 'object'
            ? {
                formId: loadedCondition.formId || '',
                questionId: loadedCondition.questionId || '',
                operator: loadedCondition.operator || 'equals',
                expectedValue: loadedCondition.expectedValue || '',
              }
            : {
                formId: '',
                questionId: '',
                operator: 'equals',
                expectedValue: '',
              }
        );
        setValidityType(existingTemplate.validityType);
        setValidityYears(existingTemplate.validityYears ?? null);
        setCustomValidityDays(existingTemplate.customValidityDays ?? null);
        setIssuanceType(existingTemplate.issuanceType ?? 'IMMEDIATE');
        setReleaseDate(existingTemplate.releaseDate ?? null);
        setApplicantCanAccess(existingTemplate.applicantCanAccess ?? true);
        setManualReleaseType((existingTemplate.manualReleaseType as any) ?? 'NOT_APPLICABLE');
        setManualReleaseUserId(existingTemplate.manualReleaseUserId ?? null);
        setManualReleaseRoles(existingTemplate.manualReleaseRoles ?? []);
        const mappings = existingTemplate.fieldMappings || [];
        console.log('[DEBUG] loadTemplate: Setting fieldMappings state', {
          count: mappings.length,
          mappings: JSON.stringify(mappings, null, 2),
          userProfileFields: mappings.filter((m: CertificateFieldMapping) => m.fieldType === 'USER_PROFILE').map((m: CertificateFieldMapping) => ({
            id: m.id,
            fieldType: m.fieldType,
            userProfileField: m.userProfileField,
          })),
        });
        setFieldMappings(mappings);
        setActiveTab('condition');
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        toast.error(t('certificate.templateManager.failedToLoadTemplate'));
        console.error('[DEBUG] loadTemplate: Error loading template', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!processId) {
      toast.error(t('certificate.templateManager.processIdRequired'));
      return;
    }

    if (!templateName.trim()) {
      toast.error(t('certificate.templateManager.templateNameRequired'));
      return;
    }

    if (!templateFileUrl) {
      toast.error(t('certificate.templateManager.pleaseUploadPdfFirst'));
      setActiveTab('upload');
      return;
    }

    if (!approvalCondition || !approvalCondition.formId || !approvalCondition.questionId || 
        approvalCondition.formId.trim() === '' || approvalCondition.questionId.trim() === '') {
      toast.error(t('certificate.templateManager.pleaseConfigureApprovalCondition'));
      setActiveTab('condition');
      return;
    }

    try {
      setSaving(true);

      let templateId = template?.id;
      
      if (template) {
        await certificateService.updateTemplate(template.id, {
          name: templateName,
          templateFileUrl,
          certificateNumberFormat,
          approvalCondition,
          validityType,
          validityYears,
          customValidityDays,
          issuanceType,
          releaseDate,
          applicantCanAccess,
          manualReleaseType,
          manualReleaseUserId,
          manualReleaseRoles,
          enableCertificateGeneration: true,
        });
        templateId = template.id;
        toast.success(t('certificate.templateManager.templateUpdatedSuccessfully'));
      } else {
        const newTemplate = await certificateService.createTemplate({
          processId,
          name: templateName,
          templateFileUrl,
          certificateNumberFormat,
          approvalCondition,
          validityType,
          validityYears,
          customValidityDays,
          issuanceType,
          releaseDate,
          applicantCanAccess,
          manualReleaseType,
          manualReleaseUserId,
          manualReleaseRoles,
          enableCertificateGeneration: true,
        });
        setTemplate(newTemplate);
        templateId = newTemplate.id;
        toast.success(t('certificate.templateManager.templateCreatedSuccessfully'));
      }

      // Always save field mappings if template exists (handles both additions and deletions)
      if (templateId) {
        await saveFieldMappings(templateId);
        // Increment refresh key so the mapper child reloads localMappings from backend,
        // preventing stale temp IDs from being pushed back on the next interaction.
        setMapperRefreshKey((k) => k + 1);
      }
      
      const updatedTemplate = await certificateService.getTemplateByProcess(processId);

      // Edits after certificates were issued start a new version; say so, since
      // existing certificates keep the old design until reissued.
      if (template && updatedTemplate && updatedTemplate.version > template.version) {
        toast.info(t('certificate.templateManager.savedAsNewVersion', { version: updatedTemplate.version }), {
          action: {
            label: t('certificate.templateManager.review'),
            onClick: () => setActiveTab('issued'),
          },
          duration: 10000,
        });
      }

      if (updatedTemplate) {
        setTemplate(updatedTemplate);
        setTemplateName(updatedTemplate.name);
        setTemplateFileUrl(updatedTemplate.templateFileUrl);
        setCertificateNumberFormat(updatedTemplate.certificateNumberFormat as any);
        const loadedCondition = updatedTemplate.approvalCondition as any;
        setApprovalCondition(
          loadedCondition && typeof loadedCondition === 'object'
            ? {
                formId: loadedCondition.formId || '',
                questionId: loadedCondition.questionId || '',
                operator: loadedCondition.operator || 'equals',
                expectedValue: loadedCondition.expectedValue || '',
              }
            : {
                formId: '',
                questionId: '',
                operator: 'equals',
                expectedValue: '',
              }
        );
        setValidityType(updatedTemplate.validityType);
        setValidityYears(updatedTemplate.validityYears ?? null);
        setCustomValidityDays(updatedTemplate.customValidityDays ?? null);
        setIssuanceType(updatedTemplate.issuanceType ?? 'IMMEDIATE');
        setReleaseDate(updatedTemplate.releaseDate ?? null);
        setApplicantCanAccess(updatedTemplate.applicantCanAccess ?? true);
        setManualReleaseType((updatedTemplate.manualReleaseType as any) ?? 'NOT_APPLICABLE');
        setManualReleaseUserId(updatedTemplate.manualReleaseUserId ?? null);
        setManualReleaseRoles(updatedTemplate.manualReleaseRoles ?? []);
        setFieldMappings(updatedTemplate.fieldMappings || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('certificate.templateManager.failedToSaveTemplate'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const saveFieldMappings = async (templateIdParam?: string) => {
    const templateId = templateIdParam || template?.id;
    if (!templateId) {
      console.log('[DEBUG] saveFieldMappings: No templateId available');
      return;
    }
    
    console.log('[DEBUG] saveFieldMappings: Starting', { templateId, fieldMappingsCount: fieldMappings.length });
    console.log('[DEBUG] saveFieldMappings: Local fieldMappings', JSON.stringify(fieldMappings, null, 2));
    
    try {
      const backendMappings = await certificateService.getFieldMappings(templateId);
      console.log('[DEBUG] saveFieldMappings: Backend mappings retrieved', JSON.stringify(backendMappings, null, 2));
      
      // Safety check: Don't delete all mappings if local state is empty but backend has mappings
      // This prevents accidental deletion when state hasn't loaded properly
      if (fieldMappings.length === 0 && backendMappings.length > 0) {
        console.warn('[DEBUG] saveFieldMappings: WARNING - Local state is empty but backend has mappings. Skipping deletion to prevent data loss.');
        // Instead, reload the mappings from backend to sync state
        setFieldMappings(backendMappings);
        return;
      }
      
      const backendMappingIds = new Set(backendMappings.map(m => m.id));
      const localMappingIds = new Set(fieldMappings.map(m => m.id).filter(id => !id.startsWith('temp-')));
      const mappingsToDelete = backendMappings.filter(m => !localMappingIds.has(m.id));
      
      console.log('[DEBUG] saveFieldMappings: Mappings to delete', mappingsToDelete.length);
      
      for (const mappingToDelete of mappingsToDelete) {
        console.log('[DEBUG] saveFieldMappings: Deleting mapping', mappingToDelete.id);
        await certificateService.deleteFieldMapping(mappingToDelete.id);
      }
      
      for (const mapping of fieldMappings) {
        if (mapping.id && !mapping.id.startsWith('temp-')) {
          const updateData = {
            x: mapping.x,
            y: mapping.y,
            width: mapping.width ?? null,
            height: mapping.height ?? null,
            fontSize: mapping.fontSize ?? null,
            fontFamily: mapping.fontFamily ?? null,
            color: mapping.color ?? null,
            alignment: mapping.alignment ?? null,
            label: mapping.label ?? null,
            sourceFormId: mapping.sourceFormId ?? null,
            sourceQuestionId: mapping.sourceQuestionId ?? null,
            userProfileField: mapping.userProfileField ?? null,
          };
          console.log('[DEBUG] saveFieldMappings: Updating mapping', {
            id: mapping.id,
            fieldType: mapping.fieldType,
            userProfileField: mapping.userProfileField,
            updateData: JSON.stringify(updateData, null, 2),
          });
          const updated = await certificateService.updateFieldMapping(mapping.id, updateData);
          console.log('[DEBUG] saveFieldMappings: Updated mapping response', JSON.stringify(updated, null, 2));
        } else {
          const createData = {
            certificateTemplateId: templateId,
            fieldType: mapping.fieldType,
            x: mapping.x,
            y: mapping.y,
            width: mapping.width ?? null,
            height: mapping.height ?? null,
            fontSize: mapping.fontSize ?? null,
            fontFamily: mapping.fontFamily ?? null,
            color: mapping.color ?? null,
            alignment: mapping.alignment ?? null,
            label: mapping.label ?? null,
            sourceFormId: mapping.sourceFormId ?? null,
            sourceQuestionId: mapping.sourceQuestionId ?? null,
            userProfileField: mapping.userProfileField ?? null,
          };
          console.log('[DEBUG] saveFieldMappings: Creating mapping', {
            fieldType: mapping.fieldType,
            userProfileField: mapping.userProfileField,
            createData: JSON.stringify(createData, null, 2),
          });
          const created = await certificateService.createFieldMapping(createData);
          console.log('[DEBUG] saveFieldMappings: Created mapping response', JSON.stringify(created, null, 2));
        }
      }
      
      // Reload mappings from backend to get the latest state with all IDs
      const verifyMappings = await certificateService.getFieldMappings(templateId);
      console.log('[DEBUG] saveFieldMappings: Verification - mappings after save', JSON.stringify(verifyMappings, null, 2));
      
      // Update local state with the verified mappings from backend
      console.log('[DEBUG] saveFieldMappings: Updating local state with verified mappings', JSON.stringify(verifyMappings, null, 2));
      setFieldMappings(verifyMappings);
    } catch (error: any) {
      console.error('[DEBUG] saveFieldMappings: Error saving field mappings', error);
      console.error('[DEBUG] saveFieldMappings: Error details', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack,
      });
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-center">{t('certificate.templateManager.loadingTemplate')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('certificate.templateManager.back')}
        </Button>
        <h1 className="text-3xl font-bold">{t('certificate.templateManager.title')}</h1>
        <p className="text-gray-600 mt-2">
          {t('certificate.templateManager.description')}
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('certificate.templateManager.templateInformation')}</CardTitle>
          <CardDescription>
            {t('certificate.templateManager.templateInformationDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('certificate.templateManager.templateName')}
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('certificate.templateManager.templateNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid w-full ${template ? 'grid-cols-7' : 'grid-cols-6'}`}>
          <TabsTrigger value="upload">{t('certificate.templateManager.uploadTemplate')}</TabsTrigger>
          <TabsTrigger value="format" disabled={!templateFileUrl}>
            {t('certificate.templateManager.format')}
          </TabsTrigger>
          <TabsTrigger value="validity" disabled={!templateFileUrl}>
            {t('certificate.templateManager.validity')}
          </TabsTrigger>
          <TabsTrigger value="fields" disabled={!templateFileUrl}>
            {t('certificate.templateManager.fieldMapping')}
          </TabsTrigger>
          <TabsTrigger value="condition" disabled={!templateFileUrl}>
            {t('certificate.templateManager.approvalCondition')}
          </TabsTrigger>
          <TabsTrigger value="issuance" disabled={!templateFileUrl}>
            {t('certificate.templateManager.issuance')}
          </TabsTrigger>
          {template && (
            <TabsTrigger value="issued">
              {t('certificate.templateManager.issuedCertificates')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="upload">
          <CertificateTemplateUpload
            templateFileUrl={templateFileUrl}
            onUploadComplete={(fileUrl) => {
              setTemplateFileUrl(fileUrl);
              toast.success(t('certificate.templateUpload.templateUploadedSuccessfully'));
              setActiveTab('format');
            }}
          />
        </TabsContent>

        <TabsContent value="format">
          <CertificateFormatBuilder
            format={certificateNumberFormat.format}
            onFormatChange={(format) => {
              setCertificateNumberFormat({ format });
            }}
          />
        </TabsContent>

        <TabsContent value="validity">
          <ValidityConfiguration
            validityType={validityType}
            validityYears={validityYears}
            customValidityDays={customValidityDays}
            onValidityTypeChange={(type) => {
              setValidityType(type);
            }}
            onValidityYearsChange={(years) => {
              setValidityYears(years);
            }}
            onCustomValidityDaysChange={(days) => {
              setCustomValidityDays(days);
            }}
          />
        </TabsContent>

        <TabsContent value="issuance">
          <IssuanceConfiguration
            issuanceType={issuanceType}
            releaseDate={releaseDate}
            applicantCanAccess={applicantCanAccess}
            onApplicantCanAccessChange={setApplicantCanAccess}
            manualReleaseType={manualReleaseType}
            onManualReleaseTypeChange={setManualReleaseType}
            manualReleaseUserId={manualReleaseUserId}
            onManualReleaseUserChange={setManualReleaseUserId}
            manualReleaseRoles={manualReleaseRoles}
            onManualReleaseRolesChange={setManualReleaseRoles}
            onIssuanceTypeChange={setIssuanceType}
            onReleaseDateChange={setReleaseDate}
          />
        </TabsContent>

        <TabsContent value="fields">
          {templateFileUrl ? (
            <CertificateFieldMapper
              templateId={template?.id}
              templateFileUrl={templateFileUrl}
              processId={processId!}
              refreshKey={mapperRefreshKey}
              onMappingsChange={(mappings) => {
                setFieldMappings(mappings);
              }}
            />
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                {t('certificate.templateManager.pleaseUploadTemplateFirst')}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="condition">
          <ApprovalConditionBuilder
            processId={processId!}
            condition={approvalCondition}
            onConditionChange={(condition) => {
              setApprovalCondition(condition);
            }}
          />
        </TabsContent>

        {template && (
          <TabsContent value="issued">
            <IssuedCertificatesPanel templateId={template.id} refreshKey={mapperRefreshKey} />
          </TabsContent>
        )}
      </Tabs>

      <div className="mt-6 flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t('certificate.templateManager.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? t('certificate.templateManager.saving') : template ? t('certificate.templateManager.updateTemplate') : t('certificate.templateManager.createTemplate')}
        </Button>
      </div>
    </div>
  );
};

export default CertificateTemplateManager;
